import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface InvoiceData {
    id: string;
    userName: string;
    userEmail: string;
    amount: number;
    currency: string;
    status: string;
    plan: string;
    date: string;
}

export const generateInvoicePdf = (data: InvoiceData) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });

    const COLORS = {
        PRIMARY: '#0E1111',
        SECONDARY: '#6366F1',
        SUCCESS: '#10B981',
        BORDER: '#E2E8F0',
        TEXT_MAIN: '#1E293B',
        TEXT_MUTED: '#64748B',
        BG_LIGHT: '#F8FAFC'
    };

    const margin = 20;
    const pageWidth = 210;

    // --- HEADER ---
    doc.setFillColor(COLORS.PRIMARY);
    doc.rect(0, 0, pageWidth, 15, 'F');

    // Brand
    doc.setFont('courier', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(COLORS.PRIMARY);
    doc.text('DEVLINT', margin, 35);
    doc.setLineWidth(0.8);
    doc.line(margin, 38, margin + 30, 38);

    // Document Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(32);
    doc.text('INVOICE', margin, 65);

    // Metadata Grid (Invoice details)
    doc.setFontSize(9);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('INVOICE ID', margin, 85);
    doc.text('BILLING DATE', margin + 60, 85);
    doc.text('STATUS', margin + 110, 85);

    doc.setTextColor(COLORS.PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(data.id.substring(0, 15).toUpperCase(), margin, 92);
    doc.text(new Date(data.date).toLocaleDateString(), margin + 60, 92);
    doc.setTextColor(COLORS.SUCCESS);
    doc.text(data.status.toUpperCase(), margin + 110, 92);

    // --- BILLING INFO ---
    doc.setDrawColor(COLORS.BORDER);
    doc.line(margin, 105, pageWidth - margin, 105);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('BILL TO:', margin, 115);

    doc.setTextColor(COLORS.PRIMARY);
    doc.setFontSize(14);
    doc.text(data.userName, margin, 123);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(data.userEmail, margin, 128);

    // --- TABLE OF ITEMS ---
    const tableBody = [
        [
            `DEVLINT SUBSCRIPTION: ${data.plan.toUpperCase()} PLAN`,
            '1',
            `${data.currency} ${data.amount.toFixed(2)}`,
            `${data.currency} ${data.amount.toFixed(2)}`
        ]
    ];

    autoTable(doc, {
        startY: 145,
        head: [['DESCRIPTION', 'QTY', 'PRICE', 'TOTAL']],
        body: tableBody,
        margin: { left: margin, right: margin },
        headStyles: {
            fillColor: COLORS.PRIMARY,
            textColor: '#FFFFFF',
            fontSize: 9,
            font: 'helvetica',
            fontStyle: 'bold',
            cellPadding: 4
        },
        bodyStyles: {
            fontSize: 10,
            textColor: COLORS.TEXT_MAIN,
            cellPadding: 6
        },
        columnStyles: {
            0: { cellWidth: 'auto' },
            1: { cellWidth: 20, halign: 'center' },
            2: { cellWidth: 35, halign: 'right' },
            3: { cellWidth: 35, halign: 'right' }
        }
    });

    // --- TOTALS ---
    const finalY = (doc as any).lastAutoTable?.finalY || 180;
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('SUBTOTAL', margin + 110, finalY + 10);
    doc.text('TAX (0%)', margin + 110, finalY + 18);
    
    doc.setFontSize(12);
    doc.setTextColor(COLORS.PRIMARY);
    doc.text('TOTAL AMOUNT', margin + 110, finalY + 30);

    doc.setTextColor(COLORS.PRIMARY);
    doc.setFontSize(10);
    doc.text(`${data.currency} ${data.amount.toFixed(2)}`, pageWidth - margin, finalY + 10, { align: 'right' });
    doc.text(`${data.currency} 0.00`, pageWidth - margin, finalY + 18, { align: 'right' });
    
    doc.setFontSize(16);
    doc.setTextColor(COLORS.SECONDARY);
    doc.text(`${data.currency} ${data.amount.toFixed(2)}`, pageWidth - margin, finalY + 30, { align: 'right' });

    // --- FOOTER ---
    doc.setDrawColor(COLORS.BORDER);
    doc.line(margin, 275, pageWidth - margin, 275);
    
    doc.setFont('courier', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(COLORS.TEXT_MUTED);
    doc.text('THANK YOU FOR SUPPORTING OPEN SOURCE ARCHITECTURE ANALYSIS.', pageWidth / 2, 282, { align: 'center' });
    doc.text('DEVLINT AI ENGINE | CLOUD SERVICES | 2026', pageWidth / 2, 287, { align: 'center' });

    doc.save(`devlint-invoice-${data.id.substring(0, 8)}.pdf`);
    return true;
};
